import { Button, Col, Modal, Row, Sidepanel, Spinner2, Text } from 'components';
import { Api, theme } from 'lib';
import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import GamePicker from 'screens/Admin/screens/QuickQuiz/components/GamePicker';
import TeamPicker from 'screens/Admin/screens/QuickQuiz/components/TeamPicker';
import {
  diffRewards,
  estimatedCloseLabel,
  FANWAVE,
  FANWAVE_MODE,
  findSpanConflict,
  fmtCount,
  freeGaps,
  gameDayTimingShape,
  isTerminalStatus,
  MATCH_CLOCK_ESTIMATE,
  matchClockWaveOf,
  MATCH_MINUTE_BOUNDS,
  minutesToHoursLabel,
  opensAtHalfTime,
  orderWavesByStart,
  projectWaveSpan,
  secondHalfEstimate,
  resolveTapTargetBounds,
  REWARDS_SOURCE,
  tapQuotaEnabled,
  timingShapeLabel,
  validateTapTarget,
  WAVE_STATUS,
  WAVE_TIMING
} from '../constants';
import { appendStartLocal, scheduleBounds } from '../scheduleBounds';
import { buildUpdatePayload, fromDatetimeLocal, toDatetimeLocal } from '../waveEditPayload';
import { HALF_TIME_WAVE_MIN } from '../anchors';
import RewardsFields, {
  normalizeRewards,
  rewardsWarnings,
  toEditableRewards,
  validateRewards
} from './RewardsFields';
import {
  Badge,
  MATCH_ACCENT,
  NumberField,
  PanelHeader,
  RadioOption,
  SourceChip,
  ToggleHeader
} from './ui';

/**
 * "Create FanWave" — the full sections 1–10 form from the "Proposed mockup contents of Fan
 * Wave on CMS" design page, submitting to POST /admin/fanwave/create. On success the panel
 * swaps to a confirmation summary of the server-resolved effective rewards.
 *
 * Doubles as the Edit form (`editWave` = a wave-list row), which PUTs /admin/fanwave/update
 * with ONLY the fields the admin changed:
 *   - Switching the Game Day schedule type is just sending the new one — the API clears the
 *     other. Going back to the default clears the pair the wave currently uses (nulls for
 *     BOTH pairs is rejected as "send one, not both", so only the live pair is cleared).
 *   - A live wave's start is fixed: those inputs lock, the end stays editable, and changing
 *     the target needs the same "Apply to active FanWave?" confirmation as applyTarget.
 *   - 409 means the wave is terminal (or closed for its match) — nothing left to update, so
 *     the panel offers Delete instead.
 * The response echoes the wave's resulting schedule/target/rewards, which is handed to the
 * list to patch the row in place rather than refetching.
 */

const ModeTab = styled.button`
  flex: 1;
  height: 42px;
  border: 1px solid ${(props) => (props.active ? theme.SECONDARY : theme.GREY_30)};
  background: ${(props) => (props.active ? theme.SECONDARY : 'white')};
  color: ${(props) => (props.active ? '#fff' : theme.GREY_60)};
  font-family: CeraPro;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;

  &:first-child {
    border-radius: 10px 0 0 10px;
  }

  &:last-child {
    border-radius: 0 10px 10px 0;
    border-left: none;
  }

  &:disabled {
    cursor: default;
    opacity: ${(props) => (props.active ? 1 : 0.5)};
  }
`;

const DateInput = styled.input`
  width: 100%;
  height: 44px;
  border-radius: 8px;
  border: 1px solid ${(props) => (props.hasError ? theme.SECONDARY : theme.GREY_30)};
  padding: 0 14px;
  font-family: CeraPro;
  font-size: 14px;
  color: ${theme.GREY_80};
  background: white;

  &:focus {
    outline: none;
    border-color: ${theme.SECONDARY};
  }
`;

// Sized to sit flush with `DateInput` above it — same 44px height, radius and border.
const OpenEventSelect = styled.select`
  width: 100%;
  height: 44px;
  box-sizing: border-box;
  border-radius: 8px;
  border: 1px solid ${theme.GREY_30};
  padding: 0 12px;
  font-family: CeraPro;
  font-size: 14px;
  color: ${theme.GREY_80};
  background: white;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: ${theme.SECONDARY};
  }
`;

// Errors render as plain red text — same convention as the Quiz/Polls forms.
const WarningText = styled.div`
  color: ${theme.SECONDARY};
  font-family: CeraPro;
  font-size: 13px;
`;

// Numbered section wrapper matching the mockup's "1. FanWave Type / 2. Schedule / …" cards.
/** Blank means "no limit" / "no minimum"; anything typed must be a whole number in range. */
const validateRestart = (maxRestarts, minRemaining) => {
  if (maxRestarts !== '' && (!Number.isInteger(Number(maxRestarts)) || Number(maxRestarts) < 1)) {
    return 'Max restarts must be a whole number of at least 1, or blank for no limit';
  }
  if (
    minRemaining !== '' &&
    (!Number.isInteger(Number(minRemaining)) || Number(minRemaining) < 0)
  ) {
    return 'Minimum minutes left must be a whole number, 0 or more';
  }
  return null;
};

const FormSection = ({ number, title, description, children }) => (
  <Col noFlex wid="100%" gap="12px" marg="0 0 18px">
    <Col noFlex wid="100%">
      <Text h4 bold marg="0">
        {number}. {title}
      </Text>
      {description && (
        <Text c1 col={theme.GREY_60} marg="2px 0 0" style={{ fontSize: '12px' }}>
          {description}
        </Text>
      )}
    </Col>
    {children}
  </Col>
);

// A field the API won't let this wave change any more — shown as read-only with the reason.
const LockedLine = ({ label, value, note }) => (
  <Col noFlex wid="100%" gap="3px">
    <Text b2 bold marg="0" style={{ fontSize: 13 }}>
      {label}
    </Text>
    <Row
      center
      start
      noFlex
      pad="10px 12px"
      style={{ background: theme.GREY_10, border: `1px solid ${theme.GREY_20}`, borderRadius: 8 }}
    >
      <Text b2 marg="0" col={theme.GREY_70} style={{ fontSize: 13 }}>
        {value}
      </Text>
    </Row>
    {note && (
      <Text c1 col={theme.GREY_60} marg="0" style={{ fontSize: 11.5 }}>
        {note}
      </Text>
    )}
  </Col>
);

// The match-minute pair. Rendered under its radio while creating (hence the radio indent) and
// on its own when editing a wave that already runs on this schedule.
const MatchMinuteFields = ({ startMinute, endMinute, onStartMinute, onEndMinute, indent }) => (
  <Row start gap="18px" wid="100%" pad={indent ? '0 0 0 28px' : undefined}>
    <NumberField
      label="Start — match minute"
      description={`${MATCH_MINUTE_BOUNDS.MIN}–${MATCH_MINUTE_BOUNDS.MAX}. Blank = opens at Half-Time.`}
      value={startMinute}
      onChange={onStartMinute}
    />
    <NumberField
      label="End — match minute"
      description={`${MATCH_MINUTE_BOUNDS.MIN}–${MATCH_MINUTE_BOUNDS.MAX}. Blank = closes at Full-Time + window.`}
      value={endMinute}
      onChange={onEndMinute}
    />
  </Row>
);

/**
 * Which stretch of the match's clock the fields below may be placed in, said in words — the same
 * window the inputs carry as native min/max, so the picker greys the rest out. Silent when the
 * match holds nothing that takes time away, which is when nothing is out of bounds.
 */
const FreeWindowHint = ({ bounds }) => {
  const gap = bounds?.gap;
  if (!gap?.start && !gap?.end) return null;
  const estimate = (flag) => (flag ? ' (estimated)' : '');
  const from = gap.start ? `${fmtDateTime(gap.start)}${estimate(gap.startEstimated)}` : null;
  const until = gap.end ? `${fmtDateTime(gap.end)}${estimate(gap.endEstimated)}` : null;
  return (
    <Text c1 col={MATCH_ACCENT.rail} marg="0" style={{ fontSize: 11.5, lineHeight: 1.45 }}>
      {from && until
        ? `Free on this match: ${from} → ${until}. The times either side of it belong to the match's other FanWaves, so the pickers don't offer them.`
        : from
          ? `The match's other FanWaves run until ${from}, so the pickers start there — this wave may open the moment the one ahead closes, but not before.`
          : `The next FanWave on this match opens ${until}, so this one has to close by then.`}
    </Text>
  );
};

// The clock-window pair — same placement rule as MatchMinuteFields. Both ends are required:
// half a window is a 400, and the API takes the pair or neither.
const ClockWindowFields = ({
  startsAt,
  endsAt,
  onStartsAt,
  onEndsAt,
  hasError,
  indent,
  bounds
}) => (
  <Col noFlex wid="100%" gap="6px" pad={indent ? '0 0 0 28px' : undefined}>
    <Row wid="100%" gap="12px">
      <Col noFlex style={{ flex: 1 }}>
        <Text b2 bold marg="0 0 6px" style={{ fontSize: 13 }}>
          Appears at *
        </Text>
        <DateInput
          type="datetime-local"
          value={startsAt}
          onChange={onStartsAt}
          hasError={hasError}
          min={bounds?.startMin || undefined}
          max={bounds?.startMax || undefined}
        />
      </Col>
      <Col noFlex style={{ flex: 1 }}>
        <Text b2 bold marg="0 0 6px" style={{ fontSize: 13 }}>
          Disappears at *
        </Text>
        <DateInput
          type="datetime-local"
          value={endsAt}
          onChange={onEndsAt}
          hasError={hasError}
          min={bounds?.endMin || undefined}
          max={bounds?.endMax || undefined}
        />
      </Col>
    </Row>
    <FreeWindowHint bounds={bounds} />
    <Text c1 col={theme.GREY_60} marg="0" style={{ fontSize: 11.5 }}>
      Both are required. Times shown in your local time ({localTzLabel}); stored in UTC.
    </Text>
  </Col>
);

// The Half-Time → date-time shape: the wave opens at the whistle like a default one and closes
// at this wall-clock moment, which replaces Full-Time + X entirely.
/**
 * The end-time field for the two shapes that open on the MATCH clock and close on the wall clock:
 * the Half-Time option, and option B's second-half kickoff. `openLabel` names which whistle it
 * opens on, since that is the only thing that differs between them.
 */
const HalfTimeUntilFields = ({
  endsAt,
  onEndsAt,
  hasError,
  indent,
  bounds,
  openLabel = 'the Half-Time whistle'
}) => (
  <Col noFlex wid="100%" gap="6px" pad={indent ? '0 0 0 28px' : undefined}>
    <Col noFlex style={{ maxWidth: 300 }}>
      <Text b2 bold marg="0 0 6px" style={{ fontSize: 13 }}>
        Disappears at *
      </Text>
      <DateInput
        type="datetime-local"
        value={endsAt}
        onChange={onEndsAt}
        hasError={hasError}
        min={bounds?.endMin || undefined}
        max={bounds?.endMax || undefined}
      />
    </Col>
    <FreeWindowHint bounds={bounds} />
    <Text c1 col={theme.GREY_60} marg="0" style={{ fontSize: 11.5 }}>
      Opens at {openLabel}; closes at this exact time instead of Full-Time + the channel’s window.
      Local time ({localTzLabel}); must be in the future.
    </Text>
  </Col>
);

/** An anchored wave's close, when it is an absolute time — the `endsAt` it projects onto. */
const absoluteCloseAt = (wave) =>
  wave?.closeAnchor?.kind === 'absolute' ? wave.closeAnchor.at || null : null;

const SCHEDULE_LOCKED_NOTE =
  'A wave keeps the schedule it was created with. To run this match on a different one, delete this wave and create a new one.';

// Why the two Half-Time shapes are off the table for this match: the whistle happens once, and
// only one wave can hold the channel's session at a time. Mirrors the API's own 400.
const HALF_TIME_TAKEN_NOTE =
  'This match already has a FanWave starting at Half-Time — an additional one needs its own start and end date-times.';

/**
 * What the match already holds, shown above the shape radios once a match is picked.
 *
 * A match can run several waves back to back, so an admin adding one needs to see the ones
 * already there: which is on air (or up next), when each opens and closes, and — when the wave
 * ahead closes on the match rather than the clock — that its close is only an ESTIMATE, said in
 * the terms that wave itself runs on.
 */
const MatchWavesNote = ({ waves, matchClockWave, predecessorSpan, postFt, freeFrom }) => {
  const ordered = orderWavesByStart(waves, postFt);
  // A close the live match decides hands over at a time nobody can state exactly. Read off the
  // PROJECTION, not off a missing `endsAt`: the Half-Time default, the half-time break and a
  // match-minute wave all come back without one, so testing for that handed the other two the
  // default's sentence — a break wave was announced as closing at Full-Time + the channel's
  // window, which is the one thing that wave never does.
  const fuzzyHandover = !!matchClockWave && !!predecessorSpan?.endEstimated;
  // Option A and the half-time break have no end to set — a fixed length and the restart — so
  // the remedy the other shapes have, a wave that closes on the wall clock, does not apply.
  const closeIsSettable =
    fuzzyHandover &&
    gameDayTimingShape(matchClockWave) !== WAVE_TIMING.HALF_TIME_INTERVAL &&
    gameDayTimingShape(matchClockWave) !== WAVE_TIMING.HALF_TIME_FIXED;
  return (
    <Col
      noFlex
      wid="100%"
      pad="12px 14px"
      gap="8px"
      style={{
        background: MATCH_ACCENT.chip,
        border: `1px solid ${MATCH_ACCENT.border}`,
        borderRadius: 12
      }}
    >
      <Text b2 bold marg="0" col={MATCH_ACCENT.rail} style={{ fontSize: 13 }}>
        This match already has {ordered.length} FanWave{ordered.length === 1 ? '' : 's'}
      </Text>
      <Col noFlex wid="100%" gap="3px">
        {ordered.map((w, i) => {
          const span = projectWaveSpan(w, postFt);
          return (
            <Text
              key={w.waveId || i}
              c1
              col={theme.GREY_70}
              marg="0"
              style={{ fontSize: 11.5, lineHeight: 1.45 }}
            >
              {i + 1}. {timingShapeLabel(gameDayTimingShape(w), postFt)} —{' '}
              {span.start ? fmtDateTime(span.start) : 'kickoff unknown'} →{' '}
              {span.end ? fmtDateTime(span.end) : '—'}
              {span.endEstimated ? ' (estimated)' : ''} · {w.status}
            </Text>
          );
        })}
      </Col>
      <Text c1 col={theme.GREY_70} marg="0" style={{ fontSize: 11.5, lineHeight: 1.45 }}>
        {fuzzyHandover
          ? `The wave ahead closes at ${estimatedCloseLabel(
              matchClockWave,
              postFt
            )}, which the live match decides — about ${fmtDateTime(predecessorSpan.end)}, no more. ${
              closeIsSettable
                ? 'Start this one after that, leaving a margin — or, since a wave keeps the schedule it was created with, replace the wave ahead with one that closes at a set time of day and the handover is exact.'
                : 'Its close is not a time you set — start this one after that, leaving a margin.'
            }`
          : freeFrom
            ? `Waves must not overlap, but touching at a boundary is fine — so this match is free from ${fmtDateTime(
                freeFrom
              )}, the moment the last one closes.`
            : 'Waves must not overlap. Touching at a boundary is fine — one can end at 16:30 and the next start at 16:30.'}
      </Text>
    </Col>
  );
};

/**
 * Seed the reward inputs when editing. A scheduled wave's `rewards` is the partial override
 * it was created with, so it has to be layered back over the channel defaults to show the
 * effective rules; a live wave's is already the fully-resolved snapshot.
 */
const seedRewards = (configRewards, edit) => {
  if (!edit?.rewards) return toEditableRewards(configRewards);
  if (edit.rewardsSource === REWARDS_SOURCE.OVERRIDE) {
    return toEditableRewards({ ...(configRewards || {}), ...edit.rewards });
  }
  return toEditableRewards(edit.rewards);
};

const localTzLabel = (() => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch (e) {
    return 'local time';
  }
})();

// Route a server-side validation message to the form section it belongs to.
const classifyServerError = (err) => {
  if (/overlap|startsAt|endsAt|future|minute|schedule|window|game/i.test(err)) return 'schedule';
  if (/target/i.test(err)) return 'target';
  return 'top';
};

const validMinute = (v) => {
  const n = Number(v);
  return (
    v !== '' && Number.isInteger(n) && n >= MATCH_MINUTE_BOUNDS.MIN && n <= MATCH_MINUTE_BOUNDS.MAX
  );
};

const CreateWaveForm = ({
  visible,
  team,
  onTeamChange,
  onClose,
  onCreated,
  editWave,
  onUpdated,
  onDeleteInstead
}) => {
  const teamId = team?._id;
  const isEdit = !!editWave;
  // A running wave: its start is history, so only the end, target and rewards can move.
  const isLiveEdit = isEdit && editWave.status === WAVE_STATUS.ACTIVE;

  const [mode, setMode] = useState(FANWAVE_MODE.GAME_DAY);

  // Section 2 — Game Day schedule: the match, plus one of the four mutually exclusive timing
  // shapes (default / match minutes / Half-Time → date-time / clock window).
  const [gameId, setGameId] = useState('');
  const [shape, setShape] = useState(WAVE_TIMING.DEFAULT);

  /**
   * Which whistle a `Half-Time → set time of day` wave being EDITED actually opens on.
   *
   * Option B stores an anchor pair, and `anchorsToLegacy` keeps only its absolute close — so a B
   * wave comes back as a lone `endsAt`, exactly like a Half-Time wave, and no read endpoint
   * returns the anchors that would tell them apart. Rather than assume Half-Time and silently
   * move a B wave's open on the next save, the form asks.
   */
  const [editOpenEvent, setEditOpenEvent] = useState('half_time');
  const [startMinute, setStartMinute] = useState('');
  const [endMinute, setEndMinute] = useState('');

  // The match's other waves. One match can hold several now, and the two rules that decide
  // what this form may offer are read off them: only ONE wave per match may start on the match
  // clock, and no two waves may overlap.
  const [matchWaves, setMatchWaves] = useState([]);
  const [loadingMatchWaves, setLoadingMatchWaves] = useState(false);

  // Section 2 — the Non-Game Day window, and the Game Day clock window (same two fields:
  // both are wall-clock date-times the wave appears and disappears on).
  const [startsAt, setStartsAt] = useState('');
  const [endsAt, setEndsAt] = useState('');

  // Sections 3–8 + target, seeded from the team's resolved config.
  const [config, setConfig] = useState(null);
  const [loadingConfig, setLoadingConfig] = useState(false);
  const [target, setTarget] = useState('');
  const [rewards, setRewards] = useState(toEditableRewards(null));
  // Auto restart: a new session starts the moment the target is reached. The two limits are
  // strings so a blank field can mean "no limit" / "no minimum".
  const [autoRestart, setAutoRestart] = useState(false);
  const [maxRestarts, setMaxRestarts] = useState('');
  const [minRemaining, setMinRemaining] = useState('');

  const [saving, setSaving] = useState(false);
  const [topError, setTopError] = useState(null);
  const [scheduleError, setScheduleError] = useState(null);
  const [created, setCreated] = useState(null);

  // What the edit form was prefilled with — every update field is diffed against this so the
  // PUT carries only what the admin actually changed.
  const [original, setOriginal] = useState(null);
  // 409: the wave is terminal / closed for its match, so there is nothing left to update.
  const [terminal, setTerminal] = useState(null);
  // Retargeting a wave fans are tapping needs the explicit "Apply to active FanWave?" step.
  const [confirmTarget, setConfirmTarget] = useState(false);

  // Re-seed whenever the panel opens, the channel changes while open, or a different wave is
  // opened for editing. `editWave` is held in the parent's state, so keying the effect on the
  // wave's identity is enough — the object itself doesn't change between renders.
  const editKey = editWave ? editWave.waveId || editWave.gameId : null;
  useEffect(() => {
    if (!visible) return;
    const edit = editWave || null;
    const editShape = gameDayTimingShape(edit) || WAVE_TIMING.DEFAULT;
    // The three clock-ended shapes are the only Game Day ones that carry date-times; a Non-Game
    // Day wave always does. Option B is one of them — its OPEN is the second-half restart, but its
    // close is an absolute time, and leaving it out here is what left the edit form with an empty
    // end and no way to move it.
    const windowed =
      !!edit &&
      (edit.mode === FANWAVE_MODE.NON_GAME_DAY ||
        editShape === WAVE_TIMING.CLOCK_WINDOW ||
        editShape === WAVE_TIMING.HALF_TIME_UNTIL ||
        editShape === WAVE_TIMING.SECOND_HALF_UNTIL);

    const seededStartMinute = edit?.startMinute != null ? String(edit.startMinute) : '';
    const seededEndMinute = edit?.endMinute != null ? String(edit.endMinute) : '';
    const seededStartsAt = windowed ? toDatetimeLocal(edit.startsAt) : '';
    // An anchored wave states its close on the anchor; `anchorsToLegacy` also projects it onto
    // `endsAt`, but only for the rows that went through that projection — the create/update echo
    // this row may have been patched with carries the pair itself.
    const seededEndsAt = windowed
      ? toDatetimeLocal(edit.endsAt) || toDatetimeLocal(absoluteCloseAt(edit))
      : '';

    setMode(edit?.mode || FANWAVE_MODE.GAME_DAY);
    setGameId(edit?.gameId || '');
    setShape(editShape);
    setEditOpenEvent('half_time');
    setStartMinute(seededStartMinute);
    setEndMinute(seededEndMinute);
    setStartsAt(seededStartsAt);
    setEndsAt(seededEndsAt);
    setTopError(null);
    setScheduleError(null);
    setCreated(null);
    setSaving(false);
    setTerminal(null);
    setConfirmTarget(false);
    setOriginal(null);
    setAutoRestart(!!edit?.autoRestart);
    setMaxRestarts(edit?.maxRestarts != null ? String(edit.maxRestarts) : '');
    setMinRemaining(edit?.minRemainingMinutes ? String(edit.minRemainingMinutes) : '');

    if (!teamId) {
      setConfig(null);
      setTarget('');
      setRewards(toEditableRewards(null));
      return;
    }
    let cancelled = false;
    (async () => {
      setLoadingConfig(true);
      const res = await Api.get(`/admin/fanwave/config?teamId=${teamId}`, null, { silent: true });
      if (cancelled) return;
      if (res?.data) {
        setConfig(res.data);
        // Editing keeps the wave's own target/rewards; creating starts from the channel's.
        const seededTarget = String(edit?.tapTarget ?? res.data.tapTarget ?? '');
        const seededRewards = seedRewards(res.data.rewards, edit);
        setTarget(seededTarget);
        setRewards(seededRewards);
        if (edit) {
          setOriginal({
            shape: editShape,
            startMinute: seededStartMinute,
            endMinute: seededEndMinute,
            startsAt: seededStartsAt,
            endsAt: seededEndsAt,
            target: seededTarget,
            rewards: normalizeRewards(seededRewards),
            autoRestart: !!edit.autoRestart,
            maxRestarts: edit.maxRestarts != null ? String(edit.maxRestarts) : '',
            minRemaining: edit.minRemainingMinutes ? String(edit.minRemainingMinutes) : ''
          });
        }
      }
      setLoadingConfig(false);
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, teamId, editKey]);

  /**
   * The match's OTHER waves, loaded once a match is picked. A match can hold several waves, and
   * the API places a new one against the ones already there: at most one may start on the match
   * clock, and none may overlap. Reading them here lets the form offer only the shapes that can
   * actually be created, instead of leaving the admin to discover the rule through a 400.
   *
   * Editing skips this: an existing wave's shape is locked anyway, so there is nothing to guide.
   */
  useEffect(() => {
    if (!visible || isEdit || !teamId || !gameId || mode !== FANWAVE_MODE.GAME_DAY) {
      setMatchWaves([]);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoadingMatchWaves(true);
      const params = new URLSearchParams({
        teamId,
        mode: FANWAVE_MODE.GAME_DAY,
        limit: '100'
      });
      const res = await Api.get(`/admin/fanwave/list?${params.toString()}`, null, { silent: true });
      if (cancelled) return;
      // Only waves that can still run constrain a new one — the API weighs scheduled and active
      // waves, and ignores everything that already closed.
      setMatchWaves(
        (res?.data?.waves || []).filter((w) => w.gameId === gameId && !isTerminalStatus(w.status))
      );
      setLoadingMatchWaves(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [visible, isEdit, teamId, gameId, mode]);

  // The wave already opening on the match clock, if the match has one. Its existence is what
  // forces every additional wave onto its own start and end date-times.
  const matchClockWave = matchClockWaveOf(matchWaves);
  // Only a wave that actually opens at Half-Time takes the whistle. A second-half wave (option B)
  // opens on the match clock too, and counting it here blocked creating Fan Wave 1 whenever
  // Fan Wave 2 had been set up first.
  const halfTimeTaken =
    !isEdit &&
    (matchWaves || []).some(
      (w) => !isTerminalStatus(w.status) && opensAtHalfTime(gameDayTimingShape(w))
    );
  const postFt = config?.postFtMinutes ?? FANWAVE.DEFAULT_POST_FT_MINUTES;
  // A predecessor that closes on the match clock — the default's Full-Time + window, the break's
  // restart, a match minute — leaves the next wave no exact moment to start from. Worth naming
  // the estimate in that wave's own terms, and whether anything can make it exact.
  const predecessorSpan = matchClockWave ? projectWaveSpan(matchClockWave, postFt) : null;

  /**
   * The match's second placement rule: no two waves may overlap. It bites even when the Half-Time
   * rule doesn't — a match whose only wave is a clock window leaves the whistle free, but a
   * Half-Time wave projected from kickoff still runs straight through that window.
   *
   * Both shapes that open at the whistle are therefore checked against the match's other waves on
   * the same kickoff estimates the API uses:
   *   DEFAULT         — its whole projected span (Half-Time → Full-Time + the channel's window).
   *   HALF_TIME_UNTIL — its START only, as a zero-length span. Its end is whatever the admin
   *                     types, so the radio is ruled out only when the whistle itself lands inside
   *                     another wave; a typed end that collides is caught in `validate` instead.
   *
   * A fixture with no kickoff time can't be projected — the API stops checking there too, so
   * nothing is blocked.
   */
  // Editing loads no match waves (an existing wave's shape is locked, so there is nothing to
  // guide), which left the second-half estimate — the moment option B's end is validated against
  // and described by — with no kickoff to project from. The edited wave carries its own match.
  const kickoff = matchWaves.find((w) => w.game?.date)?.game?.date || editWave?.game?.date || null;
  const defaultSpan = kickoff ? projectWaveSpan({}, postFt, kickoff) : null;
  const defaultConflict = defaultSpan ? findSpanConflict(defaultSpan, matchWaves, postFt) : null;

  // Option B opens on the second-half restart. Estimated off kickoff like the Half-Time open, and
  // used for the same three jobs: bounding the end picker, checking overlap, and telling the admin
  // roughly when the wave will appear.
  const secondHalfStart = secondHalfEstimate(kickoff);
  const secondHalfConflict = secondHalfStart
    ? findSpanConflict({ start: secondHalfStart, end: secondHalfStart }, matchWaves, postFt)
    : null;
  const halfTimeStartConflict = defaultSpan?.start
    ? findSpanConflict({ start: defaultSpan.start, end: defaultSpan.start }, matchWaves, postFt)
    : null;

  const shapeBlocked = {
    [WAVE_TIMING.DEFAULT]: halfTimeTaken || !!defaultConflict,
    [WAVE_TIMING.HALF_TIME_UNTIL]: halfTimeTaken || !!halfTimeStartConflict,
    // Deliberately NOT gated by `halfTimeTaken`. That rule mirrors the API's old "one wave per
    // match may open on the match clock" 400, and the anchor rebuild lifted it — a wave opening at
    // the second-half kickoff alongside one that opened at Half-Time is exactly what option B is
    // for, so gating it on that rule would make the option unusable in its own use case. Only a
    // genuine overlap at the restart rules it out.
    [WAVE_TIMING.SECOND_HALF_UNTIL]: !!secondHalfConflict,
    // Opens at the whistle like DEFAULT and HALF_TIME_UNTIL, so the same "half-time is taken"
    // rule applies — a match can only give that moment to one wave.
    [WAVE_TIMING.HALF_TIME_INTERVAL]: halfTimeTaken || !!halfTimeStartConflict,
    [WAVE_TIMING.HALF_TIME_FIXED]: halfTimeTaken || !!halfTimeStartConflict
  };

  /** Why a shape is off the table, in the API's own terms. */
  const blockedReason = (candidate) => {
    if (candidate === WAVE_TIMING.SECOND_HALF_UNTIL) {
      if (!secondHalfConflict) return null;
      const span = projectWaveSpan(secondHalfConflict, postFt);
      return `The second-half kickoff (about ${fmtDateTime(
        secondHalfStart
      )}) falls inside the FanWave already configured for this match (${fmtDateTime(
        span.start
      )} → ${fmtDateTime(span.end)}${
        span.endEstimated ? ', estimated' : ''
      }), so this wave could not open. Waves must not overlap.`;
    }
    if (halfTimeTaken) return HALF_TIME_TAKEN_NOTE;
    const clash = candidate === WAVE_TIMING.DEFAULT ? defaultConflict : halfTimeStartConflict;
    if (!clash) return null;
    const span = projectWaveSpan(clash, postFt);
    const occupied = `the FanWave already configured for this match (${fmtDateTime(span.start)} → ${fmtDateTime(
      span.end
    )}${span.endEstimated ? ', estimated' : ''})`;
    const tail = ' Waves must not overlap — give this one its own start and end date-times.';
    // The default shape is ruled out by its whole run; the Half-Time → date-time shape only by
    // where it OPENS, since its end is still up to the admin.
    return candidate === WAVE_TIMING.DEFAULT
      ? `This would run ${fmtDateTime(defaultSpan.start)} → ${fmtDateTime(defaultSpan.end)} (estimated from kickoff), which overlaps ${occupied}.${tail}`
      : `The Half-Time whistle (about ${fmtDateTime(defaultSpan.start)}) falls inside ${occupied}, so this wave could not open.${tail}`;
  };

  /**
   * The span the wave being created would occupy, as the form stands right now — the candidate the
   * API's overlap check will see. Null whenever it can't be placed yet (no kickoff, or a date the
   * admin hasn't filled in), which is also when nothing can be ruled out.
   */
  const candidateSpan = () => {
    if (shape === WAVE_TIMING.CLOCK_WINDOW) {
      return {
        start: startsAt ? new Date(startsAt) : null,
        end: endsAt ? new Date(endsAt) : null
      };
    }
    if (!defaultSpan) return null;
    if (shape === WAVE_TIMING.HALF_TIME_UNTIL) {
      return { start: defaultSpan.start, end: endsAt ? new Date(endsAt) : null };
    }
    if (shape === WAVE_TIMING.HALF_TIME_INTERVAL) {
      return { start: defaultSpan.start, end: secondHalfStart };
    }
    if (shape === WAVE_TIMING.HALF_TIME_FIXED) {
      const timed = new Date(defaultSpan.start.getTime() + HALF_TIME_WAVE_MIN * 60 * 1000);
      return {
        start: defaultSpan.start,
        end: secondHalfStart && secondHalfStart < timed ? secondHalfStart : timed
      };
    }
    if (shape === WAVE_TIMING.SECOND_HALF_UNTIL) {
      return { start: secondHalfStart, end: endsAt ? new Date(endsAt) : null };
    }
    if (shape === WAVE_TIMING.MINUTES) {
      return projectWaveSpan(
        {
          startMinute: startMinute === '' ? null : Number(startMinute),
          endMinute: endMinute === '' ? null : Number(endMinute)
        },
        postFt,
        kickoff
      );
    }
    return defaultSpan;
  };

  /**
   * What the match hasn't given away yet, and the bounds that follow for the date fields.
   *
   * The window is chosen by the START being placed — the Half-Time whistle for a Half-Time → date-
   * time wave, the typed time for a clock window — so an admin filling a hole BEFORE the waves
   * already there is offered that hole, and one adding a wave after them (nothing typed yet) gets
   * the stretch past the last close. The bounds ride on the inputs as native min/max, which is
   * what greys the taken times out in the picker, rather than leaving the admin to discover them
   * through the overlap error.
   */
  const gaps = freeGaps(matchWaves, postFt);
  const boundsFor = (windowStart) => scheduleBounds(gaps, windowStart);
  // Where a wave added to this match goes by default: after everything already on it.
  const appendFromLocal = appendStartLocal(gaps);

  /**
   * Seed a clock window's start with the moment the match frees up, so the usual case — one wave
   * straight after another — is only a matter of setting the end.
   *
   * A time the admin typed is never touched. A time this seeding put there is replaced when the
   * boundary moves (picking a different match), so switching matches can't leave the previous one's
   * handover behind — hence the ref: it is what tells the two apart. `startsAt` is deliberately out
   * of the deps, so clearing the field to type another time doesn't fill it straight back in.
   *
   * Only an EXACT boundary is seeded. A predecessor whose close the match decides has no moment to
   * hand over on, and prefilling the guess would read as one — MatchWavesNote names the estimate
   * instead, in the terms that wave runs on.
   */
  const seededStart = useRef('');
  useEffect(() => {
    if (isEdit || shape !== WAVE_TIMING.CLOCK_WINDOW || !appendFromLocal) return;
    if (startsAt && startsAt !== seededStart.current) return;
    seededStart.current = appendFromLocal;
    setStartsAt(appendFromLocal);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, shape, appendFromLocal]);

  // A shape the match can no longer take must not stay selected — otherwise the admin submits
  // the one option the API is certain to reject.
  useEffect(() => {
    if (shapeBlocked[shape]) setShape(WAVE_TIMING.CLOCK_WINDOW);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shapeBlocked[WAVE_TIMING.DEFAULT], shapeBlocked[WAVE_TIMING.HALF_TIME_UNTIL], shape]);

  const targetBounds = resolveTapTargetBounds(config);
  const targetError = target === '' ? null : validateTapTarget(target, targetBounds);
  const restartError = autoRestart ? validateRestart(maxRestarts, minRemaining) : null;

  // The schedule fields a live wave may still move: its end, in the terms it already runs on.
  // Keyed off `shape` — seeded from the row the moment the panel opens — and NOT off `original`,
  // which only exists once GET /config resolves: a clock-ended wave would otherwise be offered
  // an end MATCH MINUTE until then, and forever if that call fails.
  // Option B belongs here as well: the API refuses an anchor pair on a running wave, and its close
  // is absolute, so it travels on `endsAt` exactly like the other two.
  const liveEndsOnClock =
    isLiveEdit &&
    (shape === WAVE_TIMING.CLOCK_WINDOW ||
      shape === WAVE_TIMING.HALF_TIME_UNTIL ||
      shape === WAVE_TIMING.SECOND_HALF_UNTIL);
  const liveEndsOnMinute = isLiveEdit && shape === WAVE_TIMING.MINUTES;

  // Misconfigurations the API accepts but that leave tiers or cycles no fan can reach, or let
  // one fan claim a payout that scales with the whole community's target. An empty target field
  // means the wave inherits the channel's, so warn against THAT — the exposure is the number the
  // wave will actually run on, not the one typed here.
  const effectiveTarget = target === '' ? config?.tapTarget : target;
  const warnings = rewardsWarnings(rewards, effectiveTarget);

  // Tightening a running wave's tap quota takes effect at once: a fan already past the new
  // number is out of taps the moment this saves. Includes capping a wave that was uncapped.
  const nextQuota = normalizeRewards(rewards).maxTapsPerUser;
  const priorQuota = original?.rewards?.maxTapsPerUser ?? 0;
  const quotaTightened =
    isLiveEdit && nextQuota > 0 && (priorQuota === 0 || nextQuota < priorQuota);

  const validate = () => {
    if (!teamId) return { top: 'Pick a channel first' };
    // A live wave's start is fixed, so only its end is validated — against the start it
    // already has, which the form shows read-only.
    if (isLiveEdit) {
      if (mode === FANWAVE_MODE.NON_GAME_DAY || liveEndsOnClock) {
        if (!endsAt) return { schedule: 'An end date-time is required' };
        if (original?.startsAt && new Date(endsAt) <= new Date(original.startsAt)) {
          return { schedule: 'End must be after the start this wave already opened on' };
        }
        if (new Date(endsAt) <= new Date()) return { schedule: 'End must be in the future' };
      } else if (liveEndsOnMinute && endMinute !== '') {
        if (!validMinute(endMinute)) {
          return {
            schedule: `End match minute must be ${MATCH_MINUTE_BOUNDS.MIN}–${MATCH_MINUTE_BOUNDS.MAX}`
          };
        }
        if (startMinute !== '' && Number(endMinute) <= Number(startMinute)) {
          return { schedule: 'End match minute must be after the start match minute' };
        }
      }
      if (targetError) return { top: targetError };
      if (target === '') return { top: 'Target taps is required' };
      const liveRewardsError = validateRewards(rewards, effectiveTarget);
      if (liveRewardsError) return { top: liveRewardsError };
      return null;
    }
    if (mode === FANWAVE_MODE.GAME_DAY) {
      if (!gameId) return { schedule: 'Pick the match this FanWave is for' };
      if (shape === WAVE_TIMING.MINUTES) {
        if (startMinute === '' && endMinute === '') {
          return { schedule: 'Enter a start match minute, an end match minute, or both' };
        }
        if (startMinute !== '' && !validMinute(startMinute)) {
          return {
            schedule: `Start match minute must be ${MATCH_MINUTE_BOUNDS.MIN}–${MATCH_MINUTE_BOUNDS.MAX}`
          };
        }
        if (endMinute !== '' && !validMinute(endMinute)) {
          return {
            schedule: `End match minute must be ${MATCH_MINUTE_BOUNDS.MIN}–${MATCH_MINUTE_BOUNDS.MAX}`
          };
        }
        if (startMinute !== '' && endMinute !== '' && Number(endMinute) <= Number(startMinute)) {
          return { schedule: 'End match minute must be after the start match minute' };
        }
      }
      if (shape === WAVE_TIMING.CLOCK_WINDOW) {
        // A wall-clock START needs an end — that pair is all-or-nothing, and half of it is a 400.
        if (!startsAt || !endsAt) {
          return { schedule: 'A clock window needs both a start and an end date-time' };
        }
        if (new Date(endsAt) <= new Date(startsAt)) return { schedule: 'End must be after start' };
        if (new Date(endsAt) <= new Date()) return { schedule: 'End must be in the future' };
      }
      if (shape === WAVE_TIMING.HALF_TIME_UNTIL) {
        // The end alone IS this schedule — the start is the Half-Time whistle.
        if (!endsAt) return { schedule: 'An end date-time is required' };
        if (new Date(endsAt) <= new Date()) return { schedule: 'End must be in the future' };
      }
      if (shape === WAVE_TIMING.SECOND_HALF_UNTIL) {
        // Option B: the open is the second-half restart, so the admin only sets the end.
        if (!endsAt) return { schedule: 'An end date-time is required' };
        if (new Date(endsAt) <= new Date()) return { schedule: 'End must be in the future' };
        // The end has to land after the restart it is measured from — an estimate, so this is a
        // sanity check rather than a promise, and only made when the fixture can be placed at all.
        if (secondHalfStart && new Date(endsAt) <= secondHalfStart) {
          return {
            schedule: `The end must be after the second-half kickoff (about ${fmtDateTime(
              secondHalfStart
            )}), or the wave would close before it opened.`
          };
        }
      }
      // The window the admin actually typed, against the match's other waves. The radios rule out
      // the shapes that can never fit; this catches the times that don't, before the API 400s.
      const typedConflict = findSpanConflict(candidateSpan(), matchWaves, postFt);
      if (typedConflict) {
        const clash = projectWaveSpan(typedConflict, postFt);
        return {
          schedule: `This window overlaps the FanWave already configured for this match (${fmtDateTime(
            clash.start
          )} → ${fmtDateTime(clash.end)}${clash.endEstimated ? ', estimated' : ''}). Waves may touch at a boundary, but not overlap.`
        };
      }
    } else {
      if (!startsAt || !endsAt) return { schedule: 'Start and end date-times are required' };
      if (new Date(endsAt) <= new Date(startsAt)) return { schedule: 'End must be after start' };
      if (new Date(endsAt) <= new Date()) return { schedule: 'End must be in the future' };
    }
    if (targetError) return { top: targetError };
    if (target === '') return { top: 'Target taps is required' };
    const rewardsError = validateRewards(rewards, effectiveTarget);
    if (rewardsError) return { top: rewardsError };
    if (restartError) return { top: restartError };
    return null;
  };

  const handleUpdate = async (confirmed = false) => {
    const localErr = validate();
    if (localErr) {
      setTopError(localErr.top || null);
      setScheduleError(localErr.schedule || null);
      return;
    }
    // The diffing rules live in ../waveEditPayload — see the note there on why a blank side is
    // sent as null and why only one Game Day pair may travel.
    const { payload, changed, targetChanged } = buildUpdatePayload({
      mode,
      isLive: isLiveEdit,
      wave: editWave,
      original,
      current: {
        shape,
        startMinute,
        endMinute,
        startsAt,
        endsAt,
        target,
        rewards: normalizeRewards(rewards),
        autoRestart,
        maxRestarts,
        minRemaining
      },
      configRewards: config?.rewards
    });
    // A `Half-Time → set time of day` wave that has NOT opened is re-sent as an anchor pair rather
    // than a bare `endsAt`. Two reasons, both about not losing the open: PUT /update clears a
    // wave's anchors the moment a legacy schedule field arrives, and a lone `endsAt` always means
    // the Half-Time open — so an option B wave edited this way would quietly become a Half-Time
    // wave. The pair states the open explicitly, and is equivalent for a genuine Half-Time wave.
    //
    // A LIVE wave is excluded: the API refuses an anchor pair on one outright (its start is fixed),
    // so its end has to keep travelling on `endsAt`.
    // Fires when the END moved, and ALSO when only the open was re-pointed — that second case has
    // no legacy field to show up in `changed`, so without it selecting "2nd Half kickoff" and
    // saving would do nothing at all.
    const openRepointed = shape === WAVE_TIMING.HALF_TIME_UNTIL && editOpenEvent === 'second_half';
    // Option B is the same case with its open already known: the row's anchors state it, so there
    // is nothing to ask and nothing to re-point — but the pair still has to travel, or the bare
    // `endsAt` the diff produced would move the wave's open back to the Half-Time whistle.
    const anchoredOpenEvent =
      shape === WAVE_TIMING.SECOND_HALF_UNTIL ? 'second_half' : editOpenEvent;
    if (
      payload &&
      (shape === WAVE_TIMING.HALF_TIME_UNTIL || shape === WAVE_TIMING.SECOND_HALF_UNTIL) &&
      !isLiveEdit &&
      (changed.includes('endsAt') || openRepointed)
    ) {
      payload.openAnchor = { kind: 'match_event', event: anchoredOpenEvent };
      // From form state, not from the patch: the patch carries an end only when it CHANGED, and
      // re-pointing the open alone still has to re-send the close it keeps (anchors are a pair).
      payload.closeAnchor = { kind: 'absolute', at: fromDatetimeLocal(endsAt) };
      delete payload.endsAt;
      delete payload.startsAt;
      delete payload.startMinute;
      delete payload.endMinute;
      if (!changed.includes('openAnchor')) changed.push('openAnchor', 'closeAnchor');
    }

    if (!changed.length) {
      setScheduleError(null);
      setTopError('Nothing has changed yet — edit a field before saving.');
      return;
    }
    // Two changes hit fans mid-tap and need saying out loud first: retargeting rescales every
    // connected fan's progress bar (the API demands its own `confirm` for it), and tightening
    // the tap quota cuts off fans who have already spent the new budget.
    if (isLiveEdit && (targetChanged || quotaTightened) && !confirmed) {
      setTopError(null);
      setScheduleError(null);
      setConfirmTarget(true);
      return;
    }
    if (isLiveEdit && targetChanged) payload.confirm = true;

    setConfirmTarget(false);
    setTopError(null);
    setScheduleError(null);
    setTerminal(null);
    setSaving(true);
    const res = await Api.put('/admin/fanwave/update', payload, null, { silent: true });
    setSaving(false);

    if (!res?.data) {
      const apiErr = res?.err || 'Could not save the wave.';
      // 409 — completed/cancelled/expired, or closed for its match: there is nothing left to
      // update, so the only way forward is deleting it.
      if (res?.status === 409) {
        setTerminal(apiErr);
        return;
      }
      // 400 (overlap naming the conflicting fixture, bad window, live start moved, target out
      // of range) and 404 messages are written for admins — verbatim.
      if (classifyServerError(apiErr) === 'schedule') setScheduleError(apiErr);
      else setTopError(apiErr);
      return;
    }
    setCreated(res.data);
    // The response echoes the resulting schedule/target/rewards, so the list patches the row
    // in place instead of refetching.
    onUpdated && onUpdated(res.data);
  };

  const handleSubmit = async () => {
    if (isEdit) return handleUpdate();
    const localErr = validate();
    if (localErr) {
      setTopError(localErr.top || null);
      setScheduleError(localErr.schedule || null);
      return;
    }
    setTopError(null);
    setScheduleError(null);
    setSaving(true);

    const payload = { teamId, mode };
    if (mode === FANWAVE_MODE.GAME_DAY) {
      payload.gameId = gameId;
      // Exactly one timing shape goes on the wire; the default sends neither pair.
      if (shape === WAVE_TIMING.MINUTES) {
        if (startMinute !== '') payload.startMinute = Number(startMinute);
        if (endMinute !== '') payload.endMinute = Number(endMinute);
      } else if (shape === WAVE_TIMING.CLOCK_WINDOW) {
        payload.startsAt = fromDatetimeLocal(startsAt);
        payload.endsAt = fromDatetimeLocal(endsAt);
      } else if (shape === WAVE_TIMING.HALF_TIME_UNTIL) {
        // `endsAt` alone: the Half-Time start with a wall-clock end. A `startsAt` is exactly
        // what must NOT go with it — that would move the start off the whistle.
        payload.endsAt = fromDatetimeLocal(endsAt);
      } else if (shape === WAVE_TIMING.SECOND_HALF_UNTIL) {
        // OPTION B is the one shape the four legacy fields cannot express: there is no field that
        // says "open at the second-half kickoff" (a bare `endsAt` means the HALF-TIME open). So it
        // travels as an ANCHOR PAIR — and anchors may not accompany startMinute / endMinute /
        // startsAt / endsAt, which is why none of them are set here.
        payload.openAnchor = { kind: 'match_event', event: 'second_half' };
        payload.closeAnchor = { kind: 'absolute', at: fromDatetimeLocal(endsAt) };
      } else if (shape === WAVE_TIMING.HALF_TIME_INTERVAL) {
        // OPTION A — the break itself. Both edges come off the match, so the admin sets nothing;
        // an anchor pair for the same reason as option B, since no legacy field can say
        // "close at the second-half kick-off".
        payload.openAnchor = { kind: 'match_event', event: 'half_time' };
        payload.closeAnchor = { kind: 'match_event', event: 'second_half' };
      } else if (shape === WAVE_TIMING.HALF_TIME_FIXED) {
        // OPTION A — Fan Wave 1. Opens on the Half-Time whistle and closes 15 minutes after it
        // actually opened, or at the second-half kick-off if that comes first — so the break wave
        // never outlives the 15 minutes, and never holds Fan Wave 2 back after a short break.
        payload.openAnchor = { kind: 'match_event', event: 'half_time' };
        payload.closeAnchor = {
          kind: 'duration',
          durationMin: HALF_TIME_WAVE_MIN,
          untilEvent: 'second_half'
        };
      }
    } else {
      payload.startsAt = fromDatetimeLocal(startsAt);
      payload.endsAt = fromDatetimeLocal(endsAt);
    }
    // Only send what differs from the team's resolved config — omitted fields keep
    // falling back to the global/team defaults server-side.
    if (Number(target) !== config?.tapTarget) payload.target = Number(target);
    const rewardsPatch = diffRewards(config?.rewards, normalizeRewards(rewards));
    if (rewardsPatch) payload.rewards = rewardsPatch;
    if (autoRestart) {
      payload.autoRestart = true;
      if (maxRestarts !== '') payload.maxRestarts = Number(maxRestarts);
      if (minRemaining !== '') payload.minRemainingMinutes = Number(minRemaining);
    }

    const res = await Api.post('/admin/fanwave/create', payload, null, { silent: true });
    setSaving(false);

    if (!res?.data) {
      // 400 validation (bad schedule combination, overlap naming the conflicting fixture,
      // target out of range) and 404 messages are written for admins — show them verbatim.
      const apiErr = res?.err || 'Could not create the wave.';
      if (classifyServerError(apiErr) === 'schedule') setScheduleError(apiErr);
      else setTopError(apiErr);
      return;
    }
    setCreated(res.data);
    onCreated && onCreated(res.data);
  };

  const source = config?.source || {};
  const isGameDay = mode === FANWAVE_MODE.GAME_DAY;

  const headerText = created
    ? isEdit
      ? 'FanWave updated'
      : 'FanWave created'
    : isEdit
      ? 'Edit FanWave'
      : 'Create FanWave';

  return (
    <Sidepanel visible={visible} wid={760} pad="0" closeModal={() => !saving && onClose()}>
      <PanelHeader text={headerText} onClose={() => !saving && onClose()} />

      {created ? (
        <CreatedSummary created={created} team={team} onDone={onClose} isEdit={isEdit} />
      ) : (
        <Col wid="100%" pad="20px 24px 40px">
          {isEdit && (
            <Col
              noFlex
              wid="100%"
              pad="12px 14px"
              marg="0 0 18px"
              style={{ background: '#EAF2FE', border: '1px solid #D3E3FC', borderRadius: 12 }}
            >
              <Text b2 bold marg="0" col="#2563EB" style={{ fontSize: 13 }}>
                {isLiveEdit
                  ? 'Editing a wave fans are tapping right now'
                  : isGameDay
                    ? 'Editing a Game Day wave'
                    : 'Editing a scheduled wave'}
              </Text>
              <Text c1 col={theme.GREY_70} marg="4px 0 0" style={{ fontSize: 12, lineHeight: 1.5 }}>
                {isLiveEdit
                  ? 'The wave has already opened, so its start is fixed. You can still move where it ends and change the target and rewards — connected fans see the change immediately.'
                  : 'Only the fields you change are sent. Switching the schedule type replaces the old one.'}
              </Text>
            </Col>
          )}

          {/* 409 — the wave is spent, so there is nothing left to update. */}
          {terminal && (
            <Col
              noFlex
              wid="100%"
              pad="12px 14px"
              marg="0 0 18px"
              gap="10px"
              style={{ background: '#FDECEC', border: '1px solid #F8D4D4', borderRadius: 12 }}
            >
              <Text b2 bold marg="0" col="#DC2626" style={{ fontSize: 13, lineHeight: 1.5 }}>
                {terminal}
              </Text>
              <Row center start gap="10px" noFlex>
                {onDeleteInstead && (
                  <Button
                    text="Delete instead"
                    onClick={() => onDeleteInstead(editWave)}
                    bg={theme.SECONDARY}
                    small
                    wid="150px"
                  />
                )}
                <Button text="Close" onClick={onClose} bordered small wid="110px" />
              </Row>
            </Col>
          )}

          {/* Channel — the create endpoint is per-team, so it anchors everything below. */}
          <Col noFlex wid="100%" marg="0 0 18px">
            <Text h4 bold marg="0 0 8px">
              Channel
            </Text>
            {isEdit ? (
              <Text b2 marg="0" col={theme.GREY_70}>
                {team?.name || team?.username || teamId}
              </Text>
            ) : (
              <TeamPicker team={team} onChange={onTeamChange} label={null} />
            )}
          </Col>

          {teamId && loadingConfig ? (
            <Row center pad="24px 0">
              <Spinner2 />
            </Row>
          ) : !teamId ? (
            <Col
              centerAll
              noFlex
              wid="100%"
              pad="36px 0"
              style={{
                borderRadius: 12,
                border: `1px dashed ${theme.GREY_30}`,
                background: theme.GREY_10
              }}
            >
              <Text c1 col={theme.GREY_60} marg="0" style={{ fontSize: 12.5 }}>
                Pick a channel to configure its FanWave.
              </Text>
            </Col>
          ) : (
            <>
              {/* ——— 1. FanWave Type ——— */}
              <FormSection
                number={1}
                title="FanWave Type"
                description={
                  isEdit
                    ? 'A wave keeps its type — delete it and create a new one to switch.'
                    : 'Choose how this FanWave campaign will run.'
                }
              >
                <Row noFlex wid="100%">
                  <ModeTab
                    type="button"
                    active={!isGameDay}
                    disabled={isEdit}
                    onClick={() => !isEdit && setMode(FANWAVE_MODE.NON_GAME_DAY)}
                  >
                    Non-Game Day
                  </ModeTab>
                  <ModeTab
                    type="button"
                    active={isGameDay}
                    disabled={isEdit}
                    onClick={() => !isEdit && setMode(FANWAVE_MODE.GAME_DAY)}
                  >
                    Game Day
                  </ModeTab>
                </Row>
              </FormSection>

              {/* ——— 2. Schedule ——— */}
              <FormSection
                number={2}
                title="Schedule"
                description="Set when the FanWave will start and end."
              >
                {isGameDay ? (
                  <>
                    {/* Section 10 — Match Assignment (the mockup keeps it at the end; the
                        match anchors the whole schedule, so it leads here). */}
                    <Col noFlex wid="100%">
                      <Text b2 bold marg="0 0 6px" style={{ fontSize: 13 }}>
                        Match
                      </Text>
                      {isEdit ? (
                        // The wave is bound to its match — updating can't move it to another.
                        <Text b2 marg="0" col={theme.GREY_70} style={{ fontSize: 13 }}>
                          {editWave.game
                            ? `${editWave.game.homeName || 'TBD'} vs ${
                                editWave.game.awayName || 'TBD'
                              }${
                                editWave.game.date
                                  ? ` — ${new Date(editWave.game.date).toLocaleString()}`
                                  : ''
                              }`
                            : 'Bound match unavailable'}
                        </Text>
                      ) : (
                        <GamePicker
                          teamId={teamId}
                          value={gameId}
                          onChange={(id) => setGameId(id || '')}
                          required
                          // This form stays mounted too, so without this the match list is fetched
                          // once per page load and can outlive the fixtures it lists.
                          refreshKey={visible}
                        />
                      )}
                    </Col>

                    {/* What the match already holds. Several waves may run on one match, one
                        after another, and the rules for adding another are read off these. */}
                    {!isEdit && !!gameId && !loadingMatchWaves && matchWaves.length > 0 && (
                      <MatchWavesNote
                        waves={matchWaves}
                        matchClockWave={matchClockWave}
                        predecessorSpan={predecessorSpan}
                        postFt={postFt}
                        freeFrom={appendFromLocal}
                      />
                    )}

                    {/* The timing shapes are mutually exclusive server-side — a radio group
                        so the form can't build a combination POST /create would reject. */}
                    <Col noFlex wid="100%" gap="12px" marg="6px 0 0">
                      <Text b2 bold marg="0" style={{ fontSize: 13 }}>
                        When does this wave run?
                      </Text>

                      {isLiveEdit ? (
                        // A running wave: its start is history and the API refuses to move it.
                        // Its schedule TYPE is settled too — same rule as the scheduled branch
                        // below — so it reads as a locked line and only that type's own end is
                        // editable. A wave on the default schedule has no end of its own.
                        <>
                          <LockedLine
                            label="Runs on"
                            value={timingShapeLabel(shape, config?.postFtMinutes)}
                            note={SCHEDULE_LOCKED_NOTE}
                          />
                          <LockedLine
                            label="Start"
                            value={
                              liveEndsOnClock
                                ? `Opened ${fmtDateTime(editWave.startsAt)}`
                                : liveEndsOnMinute && startMinute !== ''
                                  ? `Match minute ${startMinute}`
                                  : 'Half-Time'
                            }
                            note="A running wave's start can no longer be moved."
                          />
                          {liveEndsOnClock ? (
                            <Col noFlex wid="100%" gap="6px" style={{ maxWidth: 300 }}>
                              <Text b2 bold marg="0" style={{ fontSize: 13 }}>
                                Disappears at *
                              </Text>
                              <DateInput
                                type="datetime-local"
                                value={endsAt}
                                onChange={(e) => setEndsAt(e.target.value)}
                                hasError={!!scheduleError}
                              />
                              <Text c1 col={theme.GREY_60} marg="0" style={{ fontSize: 11.5 }}>
                                Local time ({localTzLabel}); must be in the future.
                              </Text>
                            </Col>
                          ) : liveEndsOnMinute ? (
                            <Row start gap="18px" wid="100%">
                              <NumberField
                                label="End — match minute"
                                description={`${MATCH_MINUTE_BOUNDS.MIN}–${MATCH_MINUTE_BOUNDS.MAX}. Blank = closes at Full-Time + window.`}
                                value={endMinute}
                                onChange={setEndMinute}
                              />
                            </Row>
                          ) : shape === WAVE_TIMING.HALF_TIME_FIXED ? (
                            <LockedLine
                              label="End"
                              value={`${HALF_TIME_WAVE_MIN} minutes after it opened, or the 2nd Half kickoff if sooner`}
                              note="Fan Wave 1's end is automatic, so there is nothing to set. To stop it sooner, use Disable on its row."
                            />
                          ) : shape === WAVE_TIMING.HALF_TIME_INTERVAL ? (
                            // Option A closes at the RESTART, off the live match — not on the
                            // channel's post-Full-Time window. It shares the default's lack of an
                            // editable end and nothing else, so it cannot share its line.
                            <LockedLine
                              label="End"
                              value="2nd Half kickoff"
                              note="Both edges are read from the live match, so this wave has no end to set. To stop it sooner, use Disable on its row."
                            />
                          ) : (
                            // Giving a default wave an end match minute would make it a
                            // match-minute wave — a type switch, which is what the locked line
                            // above rules out. Its close stays the channel's window.
                            <LockedLine
                              label="End"
                              value={`Full-Time + ${minutesToHoursLabel(postFt) || `${postFt}m`}`}
                              note="A wave on the default schedule closes with the channel's window. To stop this one sooner, use Disable on its row."
                            />
                          )}
                        </>
                      ) : isEdit ? (
                        // Editing a wave that hasn't opened yet — including one QUEUED behind
                        // another, which is freely editable, start and all. Its timing SHAPE is
                        // settled (see timingShapeLabel), so it reads as a locked line and only
                        // that shape's own fields stay editable. Offering the others would
                        // invite a switch the wave can't make.
                        <>
                          <LockedLine
                            label="Runs on"
                            value={timingShapeLabel(shape, config?.postFtMinutes)}
                            note={SCHEDULE_LOCKED_NOTE}
                          />
                          {shape === WAVE_TIMING.MINUTES && (
                            <MatchMinuteFields
                              startMinute={startMinute}
                              endMinute={endMinute}
                              onStartMinute={setStartMinute}
                              onEndMinute={setEndMinute}
                            />
                          )}
                          {shape === WAVE_TIMING.HALF_TIME_UNTIL && (
                            <>
                              {/* Which whistle this wave opens on. The API returns no anchors on
                                  read, and a lone `endsAt` is what BOTH the Half-Time option and
                                  option B store — so the two are indistinguishable here. Saving
                                  writes back whichever is selected, which is why it is a control
                                  and not a label: leaving it to default would move a second-half
                                  wave's open to Half-Time the first time its end was edited. */}
                              <Col noFlex style={{ maxWidth: 300 }}>
                                <Text b2 bold marg="0 0 6px" style={{ fontSize: 13 }}>
                                  Opens at *
                                </Text>
                                <OpenEventSelect
                                  value={editOpenEvent}
                                  onChange={(e) => setEditOpenEvent(e.target.value)}
                                >
                                  <option value="half_time">Half-Time whistle</option>
                                  <option value="second_half">2nd Half kickoff</option>
                                </OpenEventSelect>
                                <Text
                                  c1
                                  col={theme.GREY_60}
                                  marg="5px 0 0"
                                  style={{ fontSize: 11.5, lineHeight: 1.45 }}
                                >
                                  Confirm which whistle this wave opens on — the API reports only
                                  its end, so the CMS cannot tell. Saving stores your choice.
                                </Text>
                              </Col>
                              <HalfTimeUntilFields
                                endsAt={endsAt}
                                onEndsAt={(e) => setEndsAt(e.target.value)}
                                hasError={!!scheduleError}
                                openLabel={
                                  editOpenEvent === 'second_half'
                                    ? 'the second-half kickoff'
                                    : 'the Half-Time whistle'
                                }
                              />
                            </>
                          )}
                          {/* Option B. Its open is stated by the anchors the row carries, so
                              unlike the Half-Time shape above there is nothing to disambiguate —
                              the open reads as a locked line and the wall-clock end stays
                              editable, which is what makes extending the wave possible at all. */}
                          {shape === WAVE_TIMING.SECOND_HALF_UNTIL && (
                            <>
                              <LockedLine
                                label="Opens at"
                                value={`2nd Half kickoff${
                                  secondHalfStart ? ` (about ${fmtDateTime(secondHalfStart)})` : ''
                                }`}
                                note="Read off the live match — the wave opens when the restart is reported, so its start is not a time to set."
                              />
                              <HalfTimeUntilFields
                                endsAt={endsAt}
                                onEndsAt={(e) => setEndsAt(e.target.value)}
                                hasError={!!scheduleError}
                                openLabel="the second-half kickoff"
                              />
                            </>
                          )}
                          {/* Option A takes no input at all: both edges come off the match. Said
                              out loud, because a schedule section with nothing under it reads as
                              a form that failed to load. The locked line above already names the
                              shape, so this states the two edges rather than repeating it. */}
                          {shape === WAVE_TIMING.HALF_TIME_INTERVAL && (
                            <LockedLine
                              label="Start → End"
                              value="Half-Time whistle → 2nd Half kickoff"
                              note="Both edges are read from the live match, so this wave has no times to set. Its target and rewards are still editable below."
                            />
                          )}
                          {shape === WAVE_TIMING.HALF_TIME_FIXED && (
                            <LockedLine
                              label="Start → End"
                              value={`Half-Time whistle → ${HALF_TIME_WAVE_MIN} minutes later, or the 2nd Half kickoff if sooner`}
                              note="Fan Wave 1's times are automatic, so there is nothing to set. Its target and rewards are still editable below."
                            />
                          )}
                          {shape === WAVE_TIMING.CLOCK_WINDOW && (
                            <ClockWindowFields
                              startsAt={startsAt}
                              endsAt={endsAt}
                              onStartsAt={(e) => setStartsAt(e.target.value)}
                              onEndsAt={(e) => setEndsAt(e.target.value)}
                              hasError={!!scheduleError}
                            />
                          )}
                        </>
                      ) : (
                        <>
                          <RadioOption
                            checked={shape === WAVE_TIMING.DEFAULT}
                            onSelect={() => setShape(WAVE_TIMING.DEFAULT)}
                            disabled={shapeBlocked[WAVE_TIMING.DEFAULT]}
                            label="Default — Half-Time → Full-Time + window"
                            description={
                              blockedReason(WAVE_TIMING.DEFAULT) ||
                              `Opens at Half-Time and closes ${postFt} minutes (${
                                minutesToHoursLabel(postFt) || `${postFt}m`
                              }) after Full-Time — the channel's configured window.`
                            }
                          />
                          {shape === WAVE_TIMING.DEFAULT && (
                            <Col noFlex wid="100%" pad="0 0 0 28px">
                              <Text
                                c1
                                col={theme.GREY_60}
                                marg="0"
                                style={{ fontSize: 11.5, lineHeight: 1.45 }}
                              >
                                Full-Time is read off the live match, so this wave’s close is an
                                estimate (roughly kickoff +{' '}
                                {minutesToHoursLabel(
                                  MATCH_CLOCK_ESTIMATE.FULL_TIME_AFTER_KICKOFF_MIN + postFt
                                )}
                                ). If a second wave is to follow it on this match, give this one an
                                exact handover point with “Half-Time → a set time of day” instead.
                              </Text>
                            </Col>
                          )}

                          {/* Match minutes is withdrawn from CREATION for now — a new wave runs
                              on the default, a Half-Time → date-time end, or a clock window. Only
                              the option is gone: the schedule itself is still a first-class shape
                              everywhere else, so a wave already on it keeps its locked line and
                              its minute fields in the two edit branches above, and the
                              list/detail surfaces still name it. Restore by uncommenting.

                          <RadioOption
                            checked={shape === WAVE_TIMING.MINUTES}
                            onSelect={() => setShape(WAVE_TIMING.MINUTES)}
                            disabled={shapeBlocked[WAVE_TIMING.MINUTES]}
                            label="Match minutes"
                            description="Read off the live match clock, not the time of day. Set a start, an end, or both — whichever you leave blank keeps its default."
                          />
                          {shape === WAVE_TIMING.MINUTES && (
                            <MatchMinuteFields
                              indent
                              startMinute={startMinute}
                              endMinute={endMinute}
                              onStartMinute={setStartMinute}
                              onEndMinute={setEndMinute}
                            />
                          )}
                          */}

                          <RadioOption
                            checked={shape === WAVE_TIMING.HALF_TIME_UNTIL}
                            onSelect={() => setShape(WAVE_TIMING.HALF_TIME_UNTIL)}
                            disabled={shapeBlocked[WAVE_TIMING.HALF_TIME_UNTIL]}
                            label="Half-Time → a set time of day"
                            description={
                              blockedReason(WAVE_TIMING.HALF_TIME_UNTIL) ||
                              'Opens at the Half-Time whistle like the default, but closes at an exact time instead of Full-Time + the window — so it can outlive the final whistle, and a second wave has a precise point to start from.'
                            }
                          />
                          {/* Opens at the whistle, so its end has to fit in whatever the match
                              leaves free from there — a stretch another wave may close off. */}
                          {shape === WAVE_TIMING.HALF_TIME_UNTIL && (
                            <HalfTimeUntilFields
                              indent
                              endsAt={endsAt}
                              onEndsAt={(e) => setEndsAt(e.target.value)}
                              hasError={!!scheduleError}
                              bounds={boundsFor(defaultSpan?.start || null)}
                            />
                          )}

                          {/* Fan Wave 1 of the Game Day pair. It replaced "Half-Time → 2nd Half
                              kickoff" here, which stayed open for as long as the feed reported HT;
                              waves already saved that way keep their own locked line above. */}
                          <RadioOption
                            checked={shape === WAVE_TIMING.HALF_TIME_FIXED}
                            onSelect={() => setShape(WAVE_TIMING.HALF_TIME_FIXED)}
                            disabled={shapeBlocked[WAVE_TIMING.HALF_TIME_FIXED]}
                            label={`Fan Wave 1 — Half-Time → ${HALF_TIME_WAVE_MIN} minutes`}
                            description={
                              blockedReason(WAVE_TIMING.HALF_TIME_FIXED) ||
                              `Opens at the Half-Time whistle and runs for ${HALF_TIME_WAVE_MIN} minutes — or closes at the 2nd Half kickoff if that comes first. Nothing to set. Pair it with “2nd Half kickoff → a set time of day” for Fan Wave 2.`
                            }
                          />

                          <RadioOption
                            checked={shape === WAVE_TIMING.SECOND_HALF_UNTIL}
                            onSelect={() => setShape(WAVE_TIMING.SECOND_HALF_UNTIL)}
                            disabled={shapeBlocked[WAVE_TIMING.SECOND_HALF_UNTIL]}
                            label="Fan Wave 2 — 2nd Half kickoff → a set time of day"
                            description={
                              blockedReason(WAVE_TIMING.SECOND_HALF_UNTIL) ||
                              `Opens when the live match reports the second-half restart${
                                secondHalfStart ? ` (about ${fmtDateTime(secondHalfStart)})` : ''
                              } — not at Half-Time — and closes at an exact time you set. Use it for a wave that should only run in the second half.`
                            }
                          />
                          {/* Opens on the restart, so its end has to fit whatever the match leaves
                              free from there — the same rule the Half-Time option follows. */}
                          {shape === WAVE_TIMING.SECOND_HALF_UNTIL && (
                            <HalfTimeUntilFields
                              indent
                              endsAt={endsAt}
                              onEndsAt={(e) => setEndsAt(e.target.value)}
                              hasError={!!scheduleError}
                              bounds={boundsFor(secondHalfStart)}
                              openLabel="the second-half kickoff"
                            />
                          )}

                          <RadioOption
                            checked={shape === WAVE_TIMING.CLOCK_WINDOW}
                            onSelect={() => setShape(WAVE_TIMING.CLOCK_WINDOW)}
                            label="Clock window"
                            description="Times of day at both ends, ignoring the match clock — the wave can open before kickoff and run past Full-Time. It stays a Game Day wave: bound to the match, with predictions on the card."
                          />
                          {shape === WAVE_TIMING.CLOCK_WINDOW && (
                            <ClockWindowFields
                              indent
                              startsAt={startsAt}
                              endsAt={endsAt}
                              onStartsAt={(e) => setStartsAt(e.target.value)}
                              onEndsAt={(e) => setEndsAt(e.target.value)}
                              hasError={!!scheduleError}
                              bounds={boundsFor(startsAt ? new Date(startsAt) : null)}
                            />
                          )}
                        </>
                      )}
                    </Col>
                  </>
                ) : (
                  <>
                    <Row wid="100%" gap="12px">
                      <Col noFlex style={{ flex: 1 }}>
                        {isLiveEdit ? (
                          <LockedLine
                            label="Starts"
                            value={fmtDateTime(editWave.startsAt)}
                            note="A running wave's start can no longer be moved."
                          />
                        ) : (
                          <>
                            <Text b2 bold marg="0 0 6px" style={{ fontSize: 13 }}>
                              Starts *
                            </Text>
                            <DateInput
                              type="datetime-local"
                              value={startsAt}
                              onChange={(e) => setStartsAt(e.target.value)}
                              hasError={!!scheduleError}
                            />
                          </>
                        )}
                      </Col>
                      <Col noFlex style={{ flex: 1 }}>
                        <Text b2 bold marg="0 0 6px" style={{ fontSize: 13 }}>
                          Ends *
                        </Text>
                        <DateInput
                          type="datetime-local"
                          value={endsAt}
                          onChange={(e) => setEndsAt(e.target.value)}
                          hasError={!!scheduleError}
                        />
                      </Col>
                    </Row>
                    <Text c1 col={theme.GREY_60} marg="0" style={{ fontSize: 11.5 }}>
                      Times shown in your local time ({localTzLabel}); stored in UTC. The window
                      can’t overlap a fixture’s expected wave window or another scheduled wave.
                    </Text>
                  </>
                )}
                {scheduleError && <WarningText>{scheduleError}</WarningText>}
              </FormSection>

              {/* ——— 3–8. Reward rules ——— */}
              <FormSection
                number="3–8"
                title="Rewards"
                description="All reward fields are optional overrides — anything you don't change keeps following the global/team defaults."
              >
                <RewardsFields
                  rewards={rewards}
                  onChange={setRewards}
                  target={target}
                  effectiveTarget={effectiveTarget}
                  onTargetChange={setTarget}
                  targetError={targetError}
                  targetDescription={`Min ${fmtCount(targetBounds.min)}, max ${fmtCount(
                    targetBounds.max
                  )}, in steps of ${fmtCount(targetBounds.step)}.`}
                />
                {/* Accepted by the API, but not what the admin is likely to mean — said here
                    rather than blocking the save (validateRewards blocks the one combination
                    that would pay out nothing at all). */}
                {warnings.map((warning, i) => (
                  <Col
                    key={i}
                    noFlex
                    wid="100%"
                    pad="10px 12px"
                    style={{
                      background: '#FFF4E5',
                      border: '1px solid #FCE4C4',
                      borderRadius: 10
                    }}
                  >
                    <Text c1 col="#B45309" marg="0" style={{ fontSize: 12, lineHeight: 1.5 }}>
                      {warning}
                    </Text>
                  </Col>
                ))}
              </FormSection>

              {/* ——— 9. Overrides & Permissions (read-only context) ——— */}
              <FormSection
                number={9}
                title="Overrides & Permissions"
                description="Where the pre-filled target comes from. Manage team overrides and the Team Admin lock in the Channel Overrides tab."
              >
                <Row center start gap="8px" style={{ flexWrap: 'wrap' }}>
                  <SourceChip
                    label="Global"
                    value={source.globalTarget}
                    unset={source.globalTarget == null}
                    winning={source.p4pTarget == null && source.teamTarget == null}
                  />
                  <SourceChip
                    label="Team"
                    value={source.teamTarget}
                    unset={source.teamTarget == null}
                    winning={source.p4pTarget == null && source.teamTarget != null}
                  />
                  <SourceChip
                    label="Override"
                    value={source.p4pTarget}
                    unset={source.p4pTarget == null}
                    winning={source.p4pTarget != null}
                  />
                  <Badge variant={config?.locked ? 'locked' : 'neutral'} dot>
                    {config?.locked ? 'Locked for Team Admin' : 'Unlocked'}
                  </Badge>
                </Row>
              </FormSection>

              {/* ——— Auto restart ——— */}
              <FormSection
                number={11}
                title="Auto Restart"
                description="Keep the channel busy when fans fill the target early."
              >
                <ToggleHeader
                  title="Restart when the target is reached"
                  description="A new session starts at once with the same target and end time. The tap count goes back to 0, and fans are paid their points when a session hands over."
                  on={autoRestart}
                  onToggle={() => setAutoRestart(!autoRestart)}
                  disabled={isLiveEdit}
                />
                {/* Fixed once the wave starts: a chain's rules must not move under fans who
                    are already tapping. The API refuses the change too. */}
                {isLiveEdit && (
                  <Text c1 col={theme.GREY_60} marg="0" style={{ fontSize: 12 }}>
                    These settings are locked because the wave has already started. They can only be
                    changed while it is scheduled.
                  </Text>
                )}
                {autoRestart && (
                  <Row start between gap="18px" wid="100%" style={{ alignItems: 'flex-start' }}>
                    <NumberField
                      label="Max restarts"
                      description="Leave blank to keep restarting until the wave ends."
                      value={maxRestarts}
                      onChange={setMaxRestarts}
                      disabled={isLiveEdit}
                    />
                    <NumberField
                      label="Minimum minutes left"
                      description="Stop restarting when less than this many minutes remain."
                      value={minRemaining}
                      onChange={setMinRemaining}
                      disabled={isLiveEdit}
                    />
                  </Row>
                )}
                {restartError && (
                  <Text c1 col={theme.SECONDARY} marg="0" style={{ fontSize: 12 }}>
                    {restartError}
                  </Text>
                )}
              </FormSection>

              {topError && <WarningText style={{ marginBottom: 14 }}>{topError}</WarningText>}

              <Row
                center
                justifyContent="flex-end"
                pad="16px 0 8px"
                style={{ borderTop: `1px solid ${theme.GREY_20}` }}
              >
                <Button
                  text="Cancel"
                  onClick={onClose}
                  bordered
                  marg="0 12px 0 0"
                  style={{ width: 'fit-content', opacity: 0.8 }}
                />
                <Button
                  text={
                    saving
                      ? isEdit
                        ? 'Saving…'
                        : 'Creating…'
                      : isEdit
                        ? 'Save changes'
                        : isGameDay
                          ? 'Create & bind to match'
                          : 'Schedule FanWave'
                  }
                  onClick={handleSubmit}
                  bg={theme.SECONDARY}
                  loading={saving}
                  disabled={saving || !!terminal}
                  style={{ width: 'fit-content' }}
                />
              </Row>
            </>
          )}
        </Col>
      )}

      {/* Changes that reach fans mid-tap — the API's `confirm: true` for a retarget, plus the
          tap-quota cut, which the API accepts silently but which closes fans out at once. */}
      <Modal
        visible={confirmTarget}
        closeModal={() => !saving && setConfirmTarget(false)}
        wid="480px"
        waiting={saving}
        noOutsideClick={saving}
      >
        <Col noFlex wid="100%" pad="28px 26px 24px" gap="14px">
          <Text h3 marg="0">
            Apply to active FanWave?
          </Text>
          {Number(target) !== Number(original?.target) && (
            <Text b2 col={theme.GREY_70} marg="0" style={{ fontSize: 13, lineHeight: 1.55 }}>
              This wave is live. Saving a new tap target of {target} broadcasts it to every
              connected fan and rescales their progress bar straight away
              {editWave?.totalHits ? ` (${editWave.totalHits} taps so far)` : ''}.
            </Text>
          )}
          {quotaTightened && (
            <Text b2 col={theme.GREY_70} marg="0" style={{ fontSize: 13, lineHeight: 1.55 }}>
              {priorQuota === 0
                ? `Fans have been tapping without a limit. Capping them at ${nextQuota} taps each takes effect immediately — anyone already past ${nextQuota} taps stops counting, for themselves and for the community total.`
                : `Lowering the tap limit from ${priorQuota} to ${nextQuota} takes effect immediately — anyone already past ${nextQuota} taps stops counting, for themselves and for the community total.`}
            </Text>
          )}
          <Row center justifyContent="flex-end" gap="10px" noFlex wid="100%">
            <Button
              text="Back"
              onClick={() => !saving && setConfirmTarget(false)}
              bordered
              disabled={saving}
              style={{ width: 'fit-content', opacity: 0.85 }}
            />
            <Button
              text={saving ? 'Saving…' : 'Apply & save'}
              onClick={() => handleUpdate(true)}
              bg={theme.SECONDARY}
              loading={saving}
              disabled={saving}
              style={{ width: 'fit-content' }}
            />
          </Row>
        </Col>
      </Modal>
    </Sidepanel>
  );
};

// ——— Post-create confirmation: the server-resolved effective rewards. ———

const SummaryRow = ({ label, value }) => (
  <Row
    center
    between
    noFlex
    wid="100%"
    pad="7px 0"
    style={{ borderBottom: `1px solid ${theme.GREY_20}` }}
  >
    <Text b2 col={theme.GREY_60} marg="0" style={{ fontSize: '12.5px' }}>
      {label}
    </Text>
    <Text b2 bold marg="0" style={{ fontSize: '12.5px', textAlign: 'right' }}>
      {value}
    </Text>
  </Row>
);

const fmtDateTime = (d) => (d ? new Date(d).toLocaleString() : '—');

const CreatedSummary = ({ created, team, onDone, isEdit }) => {
  const r = created.rewards || {};
  const isGameDay = created.mode === FANWAVE_MODE.GAME_DAY;
  // The response states the schedule the wave was stored with, and echoes its fields — read the
  // shape off `schedule`, splitting `window` by whether the wave carries a start of its own.
  const shape = gameDayTimingShape({
    ...created,
    mode: created.mode,
    status: WAVE_STATUS.SCHEDULED
  });
  const hasMinutes = shape === WAVE_TIMING.MINUTES;
  const hasWindow = shape === WAVE_TIMING.CLOCK_WINDOW;
  const halfTimeUntil = shape === WAVE_TIMING.HALF_TIME_UNTIL;
  // The two anchored shapes. They come back carrying the default's empty legacy fields and share
  // nothing else with it, so without their own branches this screen confirmed a wave the admin
  // had not created: the half-time break read back as "Default (Half-Time)", ending at Full-Time
  // + the channel's window — an hour past the restart it actually closes on.
  const secondHalfUntil = shape === WAVE_TIMING.SECOND_HALF_UNTIL;
  const halfTimeInterval = shape === WAVE_TIMING.HALF_TIME_INTERVAL;
  const halfTimeFixed = shape === WAVE_TIMING.HALF_TIME_FIXED;
  // An anchored wave's absolute close is echoed onto `endsAt`, but read the anchor too — it is
  // the field the wave was actually created with.
  const anchoredEnd = created.endsAt || created.closeAnchor?.at;
  // POST /create answers `promoted: false` when another wave still holds the match's session —
  // this one is real, but it waits its turn.
  const queued = created.promoted === false;
  return (
    <Col wid="100%" pad="20px 24px 40px" gap="16px">
      <Col
        noFlex
        wid="100%"
        pad="14px 16px"
        style={{
          background: queued ? '#EAF2FE' : '#E7FBF3',
          border: `1px solid ${queued ? '#D3E3FC' : '#C7F4E5'}`,
          borderRadius: 12
        }}
      >
        <Text b2 bold marg="0" col={queued ? '#2563EB' : '#0C9C74'}>
          {isEdit
            ? 'FanWave updated.'
            : queued
              ? 'FanWave queued behind the match’s current wave.'
              : isGameDay
                ? 'FanWave enabled for the match.'
                : 'FanWave scheduled.'}
        </Text>
        <Text c1 col={theme.GREY_70} marg="4px 0 0" style={{ fontSize: 12, lineHeight: 1.5 }}>
          {queued
            ? 'Another wave is holding this channel’s FanWave right now. This one opens as that wave closes — until then it stays fully editable, and can be cancelled from its row.'
            : isEdit && created.appliedToLiveWave === false
              ? 'Saved to the wave. It isn’t running yet, so there was nothing live to push the change onto.'
              : isEdit && created.appliedToLiveWave
                ? 'The wave is live — every connected fan already has the change.'
                : 'These are the effective rules the wave will run with — your overrides merged over the global/team defaults.'}
        </Text>
      </Col>

      <Col noFlex wid="100%">
        <Text h4 bold marg="0 0 6px">
          Wave
        </Text>
        <SummaryRow label="Channel" value={team?.name || team?.username || created.teamId} />
        <SummaryRow label="Mode" value={isGameDay ? 'Game Day' : 'Non-Game Day'} />
        {isGameDay ? (
          <>
            <SummaryRow
              label="Schedule"
              value={
                hasMinutes
                  ? 'Match minutes'
                  : hasWindow
                    ? 'Clock window'
                    : halfTimeUntil
                      ? 'Half-Time → set time of day'
                      : secondHalfUntil
                        ? '2nd Half kickoff → set time of day'
                        : halfTimeInterval
                          ? 'The half-time break'
                          : halfTimeFixed
                            ? `Fan Wave 1 — Half-Time → ${HALF_TIME_WAVE_MIN} minutes`
                            : 'Default (Half-Time)'
              }
            />
            {hasWindow ? (
              <>
                <SummaryRow label="Appears at" value={fmtDateTime(created.startsAt)} />
                <SummaryRow label="Disappears at" value={fmtDateTime(created.endsAt)} />
              </>
            ) : halfTimeUntil ? (
              <>
                <SummaryRow label="Appears at" value="Half-Time (the whistle)" />
                <SummaryRow label="Disappears at" value={fmtDateTime(anchoredEnd)} />
              </>
            ) : secondHalfUntil ? (
              <>
                <SummaryRow label="Appears at" value="2nd Half kickoff (read off the match)" />
                <SummaryRow label="Disappears at" value={fmtDateTime(anchoredEnd)} />
              </>
            ) : halfTimeInterval ? (
              <>
                <SummaryRow label="Appears at" value="Half-Time (the whistle)" />
                <SummaryRow label="Disappears at" value="2nd Half kickoff (read off the match)" />
              </>
            ) : halfTimeFixed ? (
              <>
                <SummaryRow label="Appears at" value="Half-Time (the whistle)" />
                <SummaryRow
                  label="Disappears at"
                  value={`${HALF_TIME_WAVE_MIN} minutes after it opens, or the 2nd Half kickoff if sooner`}
                />
              </>
            ) : (
              <>
                <SummaryRow
                  label="Activation"
                  value={
                    created.startMinute != null
                      ? `Match minute ${created.startMinute}`
                      : 'Half-Time (default)'
                  }
                />
                <SummaryRow
                  label="End"
                  value={
                    created.endMinute != null
                      ? `Match minute ${created.endMinute}`
                      : 'Full-Time + window (default)'
                  }
                />
              </>
            )}
          </>
        ) : (
          <>
            <SummaryRow label="Starts" value={fmtDateTime(created.startsAt)} />
            <SummaryRow label="Ends" value={fmtDateTime(created.endsAt)} />
          </>
        )}
        <SummaryRow label="Target taps" value={created.tapTarget} />
      </Col>

      <Col noFlex wid="100%">
        <Text h4 bold marg="0 0 6px">
          Effective rewards
        </Text>
        <SummaryRow
          label="Participation"
          value={`${r.tapsPerCycle} taps → ${r.basePoints} pts${r.allowMultipleCycles ? ' (repeatable)' : ' (once)'}`}
        />
        <SummaryRow
          label="Progressive rewards"
          value={
            r.progressiveEnabled
              ? (r.milestones || []).map((m) => `${m.taps}→${m.points}pts`).join(', ') || 'On'
              : 'Off'
          }
        />
        {(r.headline || r.goalMessage) && (
          <SummaryRow
            label="Goal copy"
            value={[r.headline, r.goalMessage].filter(Boolean).join(' — ')}
          />
        )}
        <SummaryRow
          label="Community bonus"
          value={
            r.communityBonusEnabled
              ? `${r.communityBonusPoints} pts (${r.bonusEligibility === 'min_taps' ? 'min. one cycle' : 'all participants'})`
              : 'Off'
          }
        />
        <SummaryRow
          label="Reward cap"
          value={
            r.capEnabled
              ? `${r.maxPoints} pts max (${r.capBehavior === 'disable' ? 'disable participation' : 'keep tapping'})`
              : 'Off'
          }
        />
        {/* The other ceiling: taps taken, not points earned. */}
        <SummaryRow
          label="Tap limit per fan"
          value={
            tapQuotaEnabled(r.maxTapsPerUser) ? `${r.maxTapsPerUser} taps per fan` : 'No tap limit'
          }
        />
        <SummaryRow
          label="Rank rewards"
          value={
            r.rankRewardsEnabled
              ? (r.rankRewards || [])
                  .map(
                    (b) =>
                      `#${b.fromRank}${b.toRank !== b.fromRank ? `–#${b.toRank}` : ''}: ${b.points}pts`
                  )
                  .join(', ') || 'On'
              : 'Off'
          }
        />
      </Col>

      <Row center justifyContent="flex-end" pad="8px 0 0">
        <Button text="Done" onClick={onDone} bg={theme.SECONDARY} style={{ width: '120px' }} />
      </Row>
    </Col>
  );
};

export default CreateWaveForm;
